import { v4 as uuidv4 } from "uuid";
import { createSlice, PayloadAction, createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { Vector3D } from "common/types";
import { tiles } from "common/model/tiles";
import { MapModel, AssetModel, AssetModelType } from "common/model";
import { MAX_UNDO_STACK_SIZE, MAP_MESH_SIZE } from "features/builder/constants";
import {
  getBuilderMaps,
  deleteBuilderMap,
  updateBuilderMap,
  createBuilderMap,
  getBuilderMapContent,
  GeneratedMapData,
} from "features/builder/api";
import {
  BuilderMap,
  BuilderState,
  ActionRecord,
  PieceStatus,
  BuilderBlockId,
  DEFAULT_ENABLED_BLOCKS,
  CompletionStepId,
  PieceEditHistoryEntry,
} from "features/builder/types";

export const mapsAdapter = createEntityAdapter<BuilderMap>();
export const { selectAll: selectAllMaps, selectById: selectMapById } = mapsAdapter.getSelectors(
  (state: RootState) => state.builder.maps,
);

export const mapModelAdapter = createEntityAdapter<MapModel>();
export const {
  selectAll: selectAllMapModel,
  selectById: selectMapModelById,
  selectIds: selectMapModelIds,
} = mapModelAdapter.getSelectors((state: RootState) => state.builder.map);

// 完成ステップ判定（spec.md §5.8.3）
export const selectCompletionSteps = createSelector(
  [
    selectAllMapModel,
    (state: RootState) => state.builder.enabledBlocks,
    (state: RootState) => state.builder.tested,
    (state: RootState) => state.builder.name,
    (state: RootState) => state.builder.description,
  ],
  (models, enabledBlocks, tested, name, description): Record<CompletionStepId, boolean> => ({
    [CompletionStepId.CHARACTER]: models.some((m) => m.type === AssetModelType.CHARACTER),
    [CompletionStepId.GOAL]: models.some((m) => m.type === AssetModelType.GOAL),
    [CompletionStepId.BLOCKS]: models.length >= 5 && enabledBlocks.length >= 1,
    [CompletionStepId.TESTED]: tested,
    [CompletionStepId.META]: name.trim().length > 0 && description.trim().length >= 10,
  }),
);

export const selectCompletionCount = createSelector(
  [selectCompletionSteps],
  (steps) => Object.values(steps).filter(Boolean).length,
);

export const selectCanComplete = createSelector([selectCompletionCount], (count) => count === 5);

const initialState: BuilderState = {
  selectedMap: null,
  maps: mapsAdapter.getInitialState(),
  loading: true,
  editingBuilderMapId: null,
  size: { x: 5, y: 5, z: 5 },
  map: mapModelAdapter.getInitialState(),
  slot: Array(5).fill(null),
  undoStacks: [],
  redoStacks: [],
  actionMenu: {
    tap: true,
    asset: false,
    eraser: false,
    assetList: false,
  },
  selectedSlotIndex: null,
  selectedAssetModel: null,
  selectedPlacedModelId: null,
  name: "",
  description: "",
  enabledBlocks: DEFAULT_ENABLED_BLOCKS,
  tested: false,
  status: PieceStatus.DRAFT_NEW,
  editHistory: [],
  draftDirty: false,
};

const builderSlice = createSlice({
  name: "builder",
  initialState,
  reducers: {
    updateSelectedMapById(state: BuilderState, action: PayloadAction<{ id: string }>) {
      const map = mapsAdapter.getSelectors().selectById(state.maps, action.payload.id);
      if (map) {
        state.selectedMap = map;
      }
    },
    updateLoading(state: BuilderState, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    resetActionMenu(state: BuilderState) {
      Object.keys(state.actionMenu).forEach((k) => (state.actionMenu[k] = false));
      state.selectedSlotIndex = null;
      state.selectedAssetModel = null;
    },
    updateActionMenu(
      state: BuilderState,
      action: PayloadAction<{
        key: string;
        value: boolean;
        exclusive?: boolean;
      }>,
    ) {
      const { key, value, exclusive } = action.payload;
      state.actionMenu[key] = value;
      if (exclusive) {
        Object.keys(state.actionMenu)
          .filter((k) => k !== key)
          .forEach((k) => (state.actionMenu[k] = false));
      }
      if (key !== "eraser") {
        state.actionMenu.eraser = false;
      }
    },
    addModelToMap(state: BuilderState, action: PayloadAction<MapModel>) {
      mapModelAdapter.addOne(state.map, action.payload);
    },
    updateMapModel(
      state: BuilderState,
      action: PayloadAction<{
        id: string;
        changes: { [item: string]: string | Vector3D };
      }>,
    ) {
      mapModelAdapter.updateOne(state.map, {
        id: action.payload.id,
        changes: action.payload.changes,
      });
    },
    removeModelFromMap(state: BuilderState, action: PayloadAction<string>) {
      mapModelAdapter.removeOne(state.map, action.payload);
    },
    updateSlot(state: BuilderState, action: PayloadAction<AssetModel[]>) {
      state.slot = action.payload;
    },
    selectSlot(state: BuilderState, action: PayloadAction<number | null>) {
      state.selectedSlotIndex = action.payload;
    },
    selectedAssetMesh(state: BuilderState, action: PayloadAction<AssetModel | null>) {
      state.selectedAssetModel = action.payload;
    },
    selectPlacedModel(state: BuilderState, action: PayloadAction<string | null>) {
      state.selectedPlacedModelId = action.payload;
    },
    // API連携なしでローカル動作させるための初期化
    initializeLocalMap(state: BuilderState) {
      state.loading = true;
      state.size = { x: 5, y: 5, z: 5 };
      state.slot = [
        {
          type: AssetModelType.DECORATION,
          name: "tree_desert",
          filename: "tree_desert.gltf.glb",
        },
        {
          type: AssetModelType.DECORATION,
          name: "tree_forest",
          filename: "tree_forest.gltf.glb",
        },
        {
          type: AssetModelType.RIVER,
          name: "tileMedium_teamBlue",
          filename: "tileMedium_teamBlue.gltf.glb",
        },
        {
          type: AssetModelType.GROUND,
          name: "tileLow_desert",
          filename: "tileLow_desert.gltf.glb",
        },
        {
          type: AssetModelType.GROUND,
          name: "tileMedium_forest",
          filename: "tileMedium_forest.gltf.glb",
        },
      ];
      state.undoStacks = [];
      state.redoStacks = [];
      state.actionMenu = {
        tap: true,
        asset: false,
        eraser: false,
        assetList: false,
      };
      state.selectedSlotIndex = null;
      state.selectedAssetModel = null;
      state.editingBuilderMapId = "local-map";
      state.name = "";
      state.description = "";
      state.enabledBlocks = DEFAULT_ENABLED_BLOCKS;
      state.tested = false;
      state.status = PieceStatus.DRAFT_NEW;
      state.editHistory = [];
      state.draftDirty = false;
      mapModelAdapter.removeAll(state.map);
    },
    // ピースビルダー: メタ・完成判定・ドラフト編集（spec.md §5.8）
    setPieceName(state: BuilderState, action: PayloadAction<string>) {
      state.name = action.payload;
    },
    setPieceDescription(state: BuilderState, action: PayloadAction<string>) {
      state.description = action.payload;
    },
    setEnabledBlocks(state: BuilderState, action: PayloadAction<BuilderBlockId[]>) {
      state.enabledBlocks = action.payload;
    },
    markTested(state: BuilderState) {
      state.tested = true;
    },
    setPieceStatus(state: BuilderState, action: PayloadAction<PieceStatus>) {
      state.status = action.payload;
    },
    pushEditHistory(state: BuilderState, action: PayloadAction<PieceEditHistoryEntry>) {
      state.editHistory.push(action.payload);
    },
    setDraftDirty(state: BuilderState, action: PayloadAction<boolean>) {
      state.draftDirty = action.payload;
    },
    enterDraftEditMode(state: BuilderState) {
      state.status = PieceStatus.EDITING;
      state.draftDirty = true;
    },
    discardDraft(state: BuilderState) {
      state.status = PieceStatus.DRAFT_NEW;
      state.draftDirty = false;
    },
    // Undo/Redo reducers
    pushUndo(state: BuilderState, action: PayloadAction<ActionRecord>) {
      state.undoStacks.push(action.payload);
      // スタックサイズ制限
      if (state.undoStacks.length > MAX_UNDO_STACK_SIZE) {
        state.undoStacks.shift();
      }
    },
    popUndo(state: BuilderState) {
      state.undoStacks.pop();
    },
    pushRedo(state: BuilderState, action: PayloadAction<ActionRecord>) {
      state.redoStacks.push(action.payload);
    },
    popRedo(state: BuilderState) {
      state.redoStacks.pop();
    },
    clearRedo(state: BuilderState) {
      state.redoStacks = [];
    },
    clearHistory(state: BuilderState) {
      state.undoStacks = [];
      state.redoStacks = [];
    },
    // AI生成マップを適用
    applyGeneratedMap(state: BuilderState, action: PayloadAction<GeneratedMapData>) {
      const data = action.payload;
      const models: MapModel[] = [];

      // タイル名からTileSetを取得するヘルパー
      const getTileByName = (name: string) => tiles.find((t) => t.name === name);

      // タイル名から高さを取得するヘルパー
      const getTileHeight = (name: string): number => {
        if (name.startsWith("tileLow")) return 1;
        if (name.startsWith("tileMedium")) return 1.5;
        if (name.startsWith("tileHigh")) return 2;
        return 1;
      };

      // 地面高さマップを作成（オブジェクト配置用）
      const groundHeightMap: Record<string, number> = {};

      // グリッド中心点オフセット
      const offset = MAP_MESH_SIZE / 2;

      // 地面タイルを追加（y: 0 に配置）
      for (const tile of data.tiles) {
        const tileSet = getTileByName(tile.name);
        if (tileSet) {
          const height = getTileHeight(tile.name);
          const key = `${tile.x}_${tile.z}`;
          groundHeightMap[key] = height;

          models.push({
            id: uuidv4(),
            name: tileSet.name,
            filename: tileSet.filename,
            type: tileSet.type,
            position: {
              x: tile.x * MAP_MESH_SIZE + offset,
              y: 0,
              z: tile.z * MAP_MESH_SIZE + offset,
            },
          });
        }
      }

      // 指定座標の地面高さを取得するヘルパー
      const getGroundHeight = (x: number, z: number): number => {
        const key = `${x}_${z}`;
        return groundHeightMap[key] ?? 1;
      };

      // キャラクター（地面の上に配置）
      const characterTile = getTileByName("character_duck");
      if (characterTile) {
        const groundHeight = getGroundHeight(data.character.x, data.character.z);
        models.push({
          id: uuidv4(),
          name: characterTile.name,
          filename: characterTile.filename,
          type: AssetModelType.CHARACTER,
          position: {
            x: data.character.x * MAP_MESH_SIZE + offset,
            y: groundHeight,
            z: data.character.z * MAP_MESH_SIZE + offset,
          },
          offset: characterTile.offset,
          scaling: characterTile.scaling,
        });
      }

      // ゴール（地面の上に配置）
      const goalTile = getTileByName("flag_teamRed");
      if (goalTile) {
        const groundHeight = getGroundHeight(data.goal.x, data.goal.z);
        models.push({
          id: uuidv4(),
          name: goalTile.name,
          filename: goalTile.filename,
          type: AssetModelType.GOAL,
          position: {
            x: data.goal.x * MAP_MESH_SIZE + offset,
            y: groundHeight,
            z: data.goal.z * MAP_MESH_SIZE + offset,
          },
        });
      }

      // 宝石（地面の上に配置）
      for (const gem of data.gems || []) {
        const gemTile = getTileByName("diamond_teamBlue");
        if (gemTile) {
          const groundHeight = getGroundHeight(gem.x, gem.z);
          models.push({
            id: uuidv4(),
            name: gemTile.name,
            filename: gemTile.filename,
            type: AssetModelType.GEM,
            position: {
              x: gem.x * MAP_MESH_SIZE + offset,
              y: groundHeight,
              z: gem.z * MAP_MESH_SIZE + offset,
            },
          });
        }
      }

      // 装飾（地面の上に配置）
      for (const deco of data.decorations || []) {
        const decoTile = getTileByName(deco.name);
        if (decoTile) {
          const groundHeight = getGroundHeight(deco.x, deco.z);
          models.push({
            id: uuidv4(),
            name: decoTile.name,
            filename: decoTile.filename,
            type: AssetModelType.DECORATION,
            position: {
              x: deco.x * MAP_MESH_SIZE + offset,
              y: groundHeight,
              z: deco.z * MAP_MESH_SIZE + offset,
            },
          });
        }
      }

      // マップをリセットして新しいモデルを設定
      mapModelAdapter.setAll(state.map, models);
      state.undoStacks = [];
      state.redoStacks = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(getBuilderMaps.fulfilled, (state: BuilderState, action: PayloadAction<any>) => {
        mapsAdapter.setAll(
          state.maps,
          action.payload.filter((item) => !item.deleted_at),
        );
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(createBuilderMap.fulfilled, (state: BuilderState, action: PayloadAction<any>) => {
        mapsAdapter.addOne(state.maps, action.payload);
        state.selectedMap = action.payload;
        state.loading = true;
        state.slot = [
          {
            type: AssetModelType.DECORATION,
            name: "tree_desert",
            filename: "tree_desert.gltf.glb",
          },
          {
            type: AssetModelType.DECORATION,
            name: "tree_forest",
            filename: "tree_forest.gltf.glb",
          },
          {
            type: AssetModelType.RIVER,
            name: "tileMedium_teamBlue",
            filename: "tileMedium_teamBlue.gltf.glb",
          },
          {
            type: AssetModelType.GROUND,
            name: "tileLow_desert",
            filename: "tileLow_desert.gltf.glb",
          },
          {
            type: AssetModelType.GROUND,
            name: "tileMedium_forest",
            filename: "tileMedium_forest.gltf.glb",
          },
        ];
        state.undoStacks = [];
        state.redoStacks = [];
        state.actionMenu = {
          tap: true,
          asset: false,
          eraser: false,
          assetList: false,
        };

        state.selectedSlotIndex = null;
        state.selectedAssetModel = null;

        state.editingBuilderMapId = action.payload.id;
        state.size = { x: 5, y: 5, z: 5 };
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(updateBuilderMap.fulfilled, (state: BuilderState, action: PayloadAction<any>) => {
        mapsAdapter.updateOne(state.maps, action.payload);
        state.selectedMap = action.payload;
      })
      .addCase(deleteBuilderMap.fulfilled, (state: BuilderState, action: PayloadAction<string>) => {
        mapsAdapter.removeOne(state.maps, action.payload);
      })
      .addCase(
        getBuilderMapContent.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: BuilderState, action: PayloadAction<any>) => {
          state.loading = true;
          state.slot = [
            {
              type: AssetModelType.DECORATION,
              name: "tree_desert",
              filename: "tree_desert.gltf.glb",
            },
            {
              type: AssetModelType.DECORATION,
              name: "tree_forest",
              filename: "tree_forest.gltf.glb",
            },
            {
              type: AssetModelType.RIVER,
              name: "tileMedium_teamBlue",
              filename: "tileMedium_teamBlue.gltf.glb",
            },
            {
              type: AssetModelType.GROUND,
              name: "tileLow_desert",
              filename: "tileLow_desert.gltf.glb",
            },
            {
              type: AssetModelType.GROUND,
              name: "tileMedium_forest",
              filename: "tileMedium_forest.gltf.glb",
            },
          ];
          state.undoStacks = [];
          state.redoStacks = [];
          state.actionMenu = {
            tap: true,
            asset: false,
            eraser: false,
            assetList: false,
          };

          state.selectedSlotIndex = null;
          state.selectedAssetModel = null;

          state.editingBuilderMapId = action.payload.id;
          const { content } = action.payload;
          state.size = content.size;
          mapModelAdapter.setAll(state.map, content.map);
        },
      );
  },
});

export const actions = { ...builderSlice.actions };

export default builderSlice.reducer;
